async function http<T>(method: string, url: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {
    // Cross-site forms cannot set custom headers — this is the CSRF defense.
    "X-Requested-With": "fetch",
  };
  const init: RequestInit = { method, credentials: "include", headers };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }
  const res = await fetch(url, init);
  let data: { error?: string } & Record<string, unknown> = {};
  try {
    data = (await res.json()) as typeof data;
  } catch {
    // non-JSON body (e.g. proxy error). Fall back to status text.
  }
  if (!res.ok) {
    throw new Error(data.error ?? `HTTP ${res.status} ${res.statusText}`.trim());
  }
  return data as T;
}

export const api = {
  get: <T>(url: string) => http<T>("GET", url),
  post: <T>(url: string, body: unknown) => http<T>("POST", url, body),
  put: <T>(url: string, body: unknown) => http<T>("PUT", url, body),
  patch: <T>(url: string, body: unknown) => http<T>("PATCH", url, body),
  delete: <T>(url: string) => http<T>("DELETE", url),
};

export interface SessionUser {
  id: number;
  username: string;
}

export const auth = {
  me: () => api.get<{ user: SessionUser | null }>("/api/auth/me"),
  login: (username: string, password: string) =>
    api.post<{ user: SessionUser }>("/api/auth/login", { username, password }),
  logout: () => api.post<{ ok: true }>("/api/auth/logout", {}),
};

export type KycStatus = "none" | "pending" | "approved";
export type InvestorStage = "prospect" | "onboarding" | "active";
export type InvestorType = "person" | "company" | "group";

export interface Investor {
  id: number;
  name: string;
  type: InvestorType;
  org_id: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  zip: string | null;
  city: string | null;
  risk_profile: number | null;
  kyc_status: KycStatus;
  kyc_reviewed_at: string | null;
  stage: InvestorStage;
  qp_client_id: string | null;
  qp_client_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface InvestorInput {
  name: string;
  type?: InvestorType;
  org_id?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  zip?: string | null;
  city?: string | null;
}

export interface ComplianceInput {
  risk_profile?: number | null;
  kyc_status?: KycStatus;
  stage?: InvestorStage;
}

export const investors = {
  updateCompliance: (id: number, input: ComplianceInput) =>
    api.patch<{ investor: Investor }>(`/api/investors/${id}/compliance`, input),
  list: (search?: string, type?: InvestorType) => {
    const qs = new URLSearchParams();
    if (search) qs.set("q", search);
    if (type) qs.set("type", type);
    const suffix = qs.toString();
    return api.get<{ investors: Investor[] }>(
      suffix ? `/api/investors?${suffix}` : "/api/investors",
    );
  },
  get: (id: number) => api.get<{ investor: Investor }>(`/api/investors/${id}`),
  create: (input: InvestorInput) =>
    api.post<{ investor: Investor }>("/api/investors", input),
  update: (id: number, input: Partial<InvestorInput>) =>
    api.patch<{ investor: Investor }>(`/api/investors/${id}`, input),
  remove: (id: number) => api.delete<{ ok: true }>(`/api/investors/${id}`),

  // Group members
  listMembers: (groupId: number) =>
    api.get<{ members: GroupMember[] }>(`/api/investors/${groupId}/members`),
  addMember: (groupId: number, memberId: number, isRepresentative = false) =>
    api.post<{ members: GroupMember[] }>(`/api/investors/${groupId}/members`, {
      member_id: memberId,
      is_representative: isRepresentative,
    }),
  setRepresentative: (
    groupId: number,
    memberId: number,
    isRepresentative: boolean,
  ) =>
    api.patch<{ members: GroupMember[] }>(
      `/api/investors/${groupId}/members/${memberId}`,
      { is_representative: isRepresentative },
    ),
  removeMember: (groupId: number, memberId: number) =>
    api.delete<{ members: GroupMember[] }>(
      `/api/investors/${groupId}/members/${memberId}`,
    ),
};

export interface GroupMember {
  member_id: number;
  is_representative: boolean;
  position: number;
  name: string;
  type: InvestorType;
  email: string | null;
  phone: string | null;
}

// ---- Quantfolio SSO jump + call log ----------------------------------------

export type JumpKind = "sso" | "session";

export interface QpFlowOption {
  id: string;
  name: string;
}

export interface QpJump {
  id: number;
  investor_id: number | null;
  kind: JumpKind;
  qp_client_id: string | null;
  flow_id: string | null;
  flow_name: string | null;
  flow_instance_id: string | null;
  jump_url: string | null;
  actor: string | null;
  status: "ok" | "failed";
  error: string | null;
  created_at: string;
}

export interface QpApiCall {
  id: number;
  jump_id: number | null;
  method: string;
  path: string;
  status: number | null;
  ok: boolean;
  duration_ms: number | null;
  request_json: string | null;
  response_json: string | null;
  error: string | null;
  created_at: string;
}

export interface QpClientFlow {
  id: string;
  name: string | null;
  status: string;
  externalStatus?: "executed" | "canceled" | null;
  startedAt?: string;
  completedAt?: string | null;
  flow?: { id?: string; name?: string };
}

export const QP_APP_BASE = "https://quantfolio.app";

export const quantfolio = {
  flows: () => api.get<{ flows: QpFlowOption[] }>("/api/quantfolio/flows"),
  investorFlows: (investorId: number) =>
    api.get<{ linked: boolean; qpClientId: string | null; flows: QpClientFlow[] }>(
      `/api/quantfolio/investors/${investorId}/flows`,
    ),
  link: (investorId: number) =>
    api.post<{ qpClientId: string }>(
      `/api/quantfolio/investors/${investorId}/link`,
      {},
    ),
  pushAccounts: (investorId: number) =>
    api.post<QpPushAccountsResult>(
      `/api/quantfolio/investors/${investorId}/push-accounts`,
      {},
    ),
  jump: (body: {
    investorId: number;
    kind: JumpKind;
    flowId?: string;
    flowName?: string;
  }) => api.post<{ jump: QpJump }>("/api/quantfolio/jump", body),
  jumps: (investorId?: number) =>
    api.get<{ jumps: QpJump[] }>(
      investorId
        ? `/api/quantfolio/jumps?investorId=${investorId}`
        : "/api/quantfolio/jumps",
    ),
  calls: (opts?: { jumpId?: number; limit?: number }) => {
    const qs = new URLSearchParams();
    if (opts?.jumpId) qs.set("jumpId", String(opts.jumpId));
    if (opts?.limit) qs.set("limit", String(opts.limit));
    const s = qs.toString();
    return api.get<{ calls: QpApiCall[] }>(
      s ? `/api/quantfolio/calls?${s}` : "/api/quantfolio/calls",
    );
  },
  clearCalls: () => api.delete<{ cleared: number }>("/api/quantfolio/calls"),

  inbox: (status?: InboxStatus) =>
    api.get<{ items: InboxItem[] }>(
      status ? `/api/quantfolio/inbox?status=${status}` : "/api/quantfolio/inbox",
    ),
  inboxSync: () =>
    api.post<{
      orders: number;
      accounts: number;
      savingsPlans: number;
      items: InboxItem[];
    }>("/api/quantfolio/inbox/sync", {}),
  inboxProcess: (
    id: number,
    action: InboxAction,
    opts?: { reason?: string; amount?: number },
  ) =>
    api.post<{ item: InboxItem }>(`/api/quantfolio/inbox/${id}/process`, {
      action,
      ...opts,
    }),
  inboxProcessBulk: (
    ids: number[],
    action: InboxAction,
    opts?: { reason?: string },
  ) =>
    api.post<InboxBulkResult>("/api/quantfolio/inbox/process-bulk", {
      ids,
      action,
      ...opts,
    }),
  inboxDismiss: (ids: number[]) =>
    api.post<{ dismissed: number; items: InboxItem[] }>(
      "/api/quantfolio/inbox/dismiss",
      { ids },
    ),
  inboxRestore: (ids: number[]) =>
    api.post<{ restored: number; items: InboxItem[] }>(
      "/api/quantfolio/inbox/restore",
      { ids },
    ),
};

export interface InboxBulkResult {
  processed: number;
  failed: number;
  skipped: number;
  results: { id: number; status: "done" | "failed" | "skipped"; error?: string }[];
  items: InboxItem[];
}

export type QpPushOperation = "updated" | "adopted" | "created";

export interface QpPushedAccount {
  accountId: number;
  name: string;
  externalId: string;
  qpAccountId: string;
  operation: QpPushOperation;
  positions: number;
}

export interface QpPushAccountsResult {
  investorId: number;
  qpClientId: string;
  created: number;
  adopted: number;
  updated: number;
  accounts: QpPushedAccount[];
}

// ---- Savings agreements (spareavtale) --------------------------------------

export type SavingsFrequency = "monthly" | "quarterly" | "yearly";

export interface SavingsPlanLine {
  id: number;
  savings_plan_id: number;
  isin: string | null;
  name: string | null;
  size_type: "amount" | "units";
  size: number;
  qp_order_id: string | null;
  line_order: number;
}

export interface SavingsPlanInstallment {
  id: number;
  savings_plan_id: number;
  due_date: string;
  run_at: string;
  status: "executed" | "partial" | "failed";
  orders_created: number;
  total_amount: number;
  error: string | null;
}

export interface SavingsPlan {
  id: number;
  investor_id: number;
  account_id: number | null;
  name: string;
  currency: string;
  frequency: SavingsFrequency;
  start_date: string;
  end_date: string | null;
  status: "active" | "cancelled";
  source: string;
  qp_client_id: string | null;
  qp_plan_id: string | null;
  qp_flow_instance_id: string | null;
  next_run_date: string | null;
  created_at: string;
  account_name: string | null;
  lines: SavingsPlanLine[];
  installments: SavingsPlanInstallment[];
}

export interface SavingsPlanPatch {
  name?: string;
  frequency?: SavingsFrequency;
  start_date?: string;
  end_date?: string | null;
  account_id?: number | null;
  lines?: { isin: string | null; name: string | null; size_type: "amount" | "units"; size: number }[];
}

export interface InstallmentResult {
  planId: number;
  planName: string;
  dueDate: string;
  status: "executed" | "partial" | "failed" | "skipped";
  ordersCreated: number;
  totalAmount: number;
  funded: number;
  lines: { isin: string | null; name: string | null; amount: number; orderId: number | null; error: string | null }[];
  error: string | null;
}

export type InboxKind = "account" | "order" | "savings_plan";
export type InboxStatus = "new" | "done" | "failed" | "ignored";
export type InboxAction =
  | "open"
  | "execute"
  | "partial"
  | "reject"
  | "cancel"
  | "post";

export interface InboxItem {
  id: number;
  kind: InboxKind;
  qp_id: string;
  qp_client_id: string | null;
  investor_id: number | null;
  flow_instance_id: string | null;
  goal_id: string | null;
  status: InboxStatus;
  summary: string | null;
  andersen_ref: string | null;
  error: string | null;
  received_at: string;
  processed_at: string | null;
}

// ---- Quantfolio API-driven mobile advice flow (M12) ------------------------
// The Investor App drives a QP flow-instance to finished advice. Config payloads
// (fields/options) are forwarded from QP verbatim, so they're typed loosely and
// the renderers read what they need. See docs/quantfolio-mobile-flow.md.

export type FlowRunStatus = "in_progress" | "submitted" | "completed" | "failed";

export interface FlowRun {
  id: number;
  investor_id: number | null;
  qp_client_id: string | null;
  flow_id: string | null;
  flow_name: string | null;
  flow_instance_id: string | null;
  status: FlowRunStatus;
  current_step: string | null;
  advice_json: string | null;
  actor: string | null;
  error: string | null;
  created_at: string;
  updated_at: string;
}

export interface FlowStep {
  status: "complete" | "open" | "not_applicable";
  kind: string;
  pageId?: string;
  pageName?: string;
  goalId?: string;
  portfolioId?: string;
  componentId?: string;
  componentType?: string;
  fieldId?: string;
  fieldName?: string;
  reason?: string;
}

export interface FlowStatus {
  status: string;
  canSubmit: boolean;
  percent: number;
  totalRequired: number;
  filledRequired: number;
  steps: FlowStep[];
}

export interface QpGoal {
  id: string;
  name: string;
  goalType: string;
  riskScore?: number | null;
  hasRiskAssessment?: boolean;
}

export interface QpLabelled {
  id: string;
  label: string;
  order?: number;
}

export interface QpRiskOptions {
  timeHorizons: QpLabelled[];
  riskLevels: QpLabelled[];
  adjustmentQuestions: {
    id: string;
    label: string;
    answers: { id: string; label: string }[];
  }[];
  cap?: unknown;
}

export interface QpRiskResult {
  score: number;
  isCapped: boolean;
  timeHorizonName: string;
  riskLevelName: string;
  selectedPortfolioId: string | null;
  modelResolutionFailure: string | null;
}

export interface QpGoalPortfolio {
  id: string;
  goalId: string;
  name: string;
  portfolioType: "model" | "custom" | "dynamic" | null;
  scheduleType?: "one_time" | "recurring" | null;
  oneTimeAmount?: number | null;
}

export interface QpPosition {
  productId: string;
  name: string;
  isin: string;
  assetClassName: string;
  weight: number;
  amount?: number;
}

export interface QpComposition {
  portfolioId: string;
  portfolioType: "model" | "custom" | "dynamic";
  modelPortfolioName?: string;
  modelResolutionFailure?: string | null;
  positions: QpPosition[];
}

export interface QpAdvice {
  goals: {
    goalId: string;
    goalName: string;
    goalType: string;
    riskScore: number | null;
    portfolioId: string | null;
    composition: QpComposition | null;
  }[];
}

export interface QpPdf {
  id: string;
  templateId: string;
  templateName: string;
  status: string;
  url: string;
  urlExpiresAt: string;
}

export type Json = Record<string, unknown>;

export const flowRun = {
  flows: () => api.get<{ flows: QpFlowOption[] }>("/api/quantfolio/flow/flows"),
  start: (investorId: number, flowId: string, flowName?: string) =>
    api.post<{ run: FlowRun }>("/api/quantfolio/flow/runs", {
      investorId,
      flowId,
      flowName,
    }),
  runs: (investorId?: number) =>
    api.get<{ runs: FlowRun[] }>(
      investorId
        ? `/api/quantfolio/flow/runs?investorId=${investorId}`
        : "/api/quantfolio/flow/runs",
    ),
  run: (id: number) => api.get<{ run: FlowRun }>(`/api/quantfolio/flow/runs/${id}`),
  status: (id: number) =>
    api.get<{ status: FlowStatus }>(`/api/quantfolio/flow/runs/${id}/status`),
  step: (id: number, component: string) =>
    api.get<{ step: Json }>(`/api/quantfolio/flow/runs/${id}/step/${component}`),
  submitStep: (id: number, component: string, body: Json) =>
    api.put<{ result: Json }>(
      `/api/quantfolio/flow/runs/${id}/step/${component}`,
      body,
    ),
  goals: (id: number) =>
    api.get<{ goals: QpGoal[] }>(`/api/quantfolio/flow/runs/${id}/goals`),
  createGoal: (id: number, body: Json) =>
    api.post<{ goal: QpGoal }>(`/api/quantfolio/flow/runs/${id}/goals`, body),
  patchGoal: (id: number, goalId: string, body: Json) =>
    api.patch<{ goal: QpGoal }>(
      `/api/quantfolio/flow/runs/${id}/goals/${goalId}`,
      body,
    ),
  portfolios: (id: number, goalId: string) =>
    api.get<{ portfolios: QpGoalPortfolio[] }>(
      `/api/quantfolio/flow/runs/${id}/goals/${goalId}/portfolios`,
    ),
  patchPortfolio: (id: number, goalId: string, portfolioId: string, body: Json) =>
    api.patch<{ portfolio: Json }>(
      `/api/quantfolio/flow/runs/${id}/goals/${goalId}/portfolios/${portfolioId}`,
      body,
    ),
  riskOptions: (id: number, goalId: string) =>
    api.get<{ options: QpRiskOptions }>(
      `/api/quantfolio/flow/runs/${id}/goals/${goalId}/risk-options`,
    ),
  submitRisk: (id: number, goalId: string, body: Json) =>
    api.put<{ result: QpRiskResult }>(
      `/api/quantfolio/flow/runs/${id}/goals/${goalId}/risk`,
      body,
    ),
  advice: (id: number) =>
    api.get<{ advice: QpAdvice }>(`/api/quantfolio/flow/runs/${id}/advice`),
  submit: (id: number) =>
    api.post<{ result: Json }>(`/api/quantfolio/flow/runs/${id}/submit`, {}),
  pdf: (id: number, templateId?: string) =>
    api.post<{ data: QpPdf[]; errors: string[] }>(
      `/api/quantfolio/flow/runs/${id}/pdf`,
      templateId ? { templateId } : {},
    ),
  pdfs: (id: number) =>
    api.get<{ pdfs: QpPdf[] }>(`/api/quantfolio/flow/runs/${id}/pdfs`),
  pdfUrl: (id: number, pdfId: string) =>
    api.get<{ url: string; expiresIn: number }>(
      `/api/quantfolio/flow/runs/${id}/pdf/${pdfId}/url`,
    ),
  calls: (id: number) =>
    api.get<{ calls: QpApiCall[] }>(`/api/quantfolio/flow/runs/${id}/calls`),
  sign: (id: number) =>
    api.post<{ jump: QpJump }>(`/api/quantfolio/flow/runs/${id}/sign`, {}),
};

export interface InvestorDocument {
  id: number;
  investor_id: number;
  filename: string;
  mime: string;
  size: number;
  storage_path: string;
  uploaded_at: string;
}

export const documents = {
  list: (investorId: number) =>
    api.get<{ documents: InvestorDocument[] }>(
      `/api/investors/${investorId}/documents`,
    ),
  upload: async (investorId: number, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`/api/investors/${investorId}/documents`, {
      method: "POST",
      credentials: "include",
      headers: { "X-Requested-With": "fetch" },
      body: fd,
    });
    let data: { document?: InvestorDocument; error?: string } = {};
    try {
      data = (await res.json()) as typeof data;
    } catch {
      /* non-JSON */
    }
    if (!res.ok || !data.document) {
      throw new Error(
        data.error ?? `HTTP ${res.status} ${res.statusText}`.trim(),
      );
    }
    return data.document;
  },
  downloadUrl: (investorId: number, id: number) =>
    `/api/investors/${investorId}/documents/${id}`,
  remove: (investorId: number, id: number) =>
    api.delete<{ ok: true }>(`/api/investors/${investorId}/documents/${id}`),
};

export type ActivityKind = "call" | "meeting" | "email";

export interface InvestorActivity {
  id: number;
  investor_id: number;
  kind: ActivityKind;
  subject: string;
  notes: string | null;
  occurred_at: string;
  created_at: string;
  updated_at: string;
}

export interface ActivityInput {
  kind: ActivityKind;
  subject: string;
  notes?: string | null;
  occurred_at: string;
}

export type AccountType = "ask" | "ips" | "ordinary";

export interface Account {
  id: number;
  investor_id: number;
  name: string;
  currency: string;
  type: AccountType;
  opened_at: string;
  mandate_bank_account: string | null;
  mandate_limit: number | null;
}

export interface AccountWithValue extends Account {
  value: number;
  securities_value: number;
  cash_balance: number;
}

export type TransactionKind =
  | "deposit"
  | "withdrawal"
  | "buy"
  | "sell"
  | "fee"
  | "dividend";

export interface AccountTransaction {
  id: number;
  account_id: number;
  kind: TransactionKind;
  amount: number;
  order_id: number | null;
  note: string | null;
  occurred_at: string;
  created_at: string;
}

export interface Position {
  id: number;
  account_id: number;
  name: string;
  isin: string;
  units: number;
  price: number;
  value: number;
  updated_at: string;
}

export interface InvestorPortfolio {
  investor_id: number;
  total_value: number;
  by_currency: Record<string, number>;
  nok: {
    total_nok: number | null;
    rates: Record<string, number>;
    missing: string[];
  };
  accounts: AccountWithValue[];
}

export interface PerformancePoint {
  date: string;
  value: number;
}

export interface AccountPerformance {
  account_id: number;
  currency: string;
  periods: Record<string, number | null>;
  series: PerformancePoint[];
  benchmark: PerformancePoint[] | null;
  benchmark_name: string | null;
}

export interface Instrument {
  id: number;
  isin: string;
  name: string;
  currency: string;
  type: string;
}

export interface OpenAccountInput {
  name: string;
  currency: string;
  type: AccountType;
  risk_profile: number;
  kyc: {
    id_verified: boolean;
    pep_checked: boolean;
    source_of_funds: boolean;
  };
  initial_deposit?: number | null;
  mandate?: { bank_account: string; limit: number } | null;
}

export const portfolio = {
  forInvestor: (investorId: number) =>
    api.get<{ portfolio: InvestorPortfolio }>(
      `/api/investors/${investorId}/portfolio`,
    ),
  positions: (accountId: number) =>
    api.get<{ account: Account; positions: Position[]; cash_balance: number }>(
      `/api/accounts/${accountId}/positions`,
    ),
  transactions: (accountId: number) =>
    api.get<{ transactions: AccountTransaction[]; cash_balance: number }>(
      `/api/accounts/${accountId}/transactions`,
    ),
  postTransaction: (
    accountId: number,
    kind: "deposit" | "withdrawal",
    amount: number,
    note?: string,
  ) =>
    api.post<{ transaction: AccountTransaction; cash_balance: number }>(
      `/api/accounts/${accountId}/transactions`,
      { kind, amount, note },
    ),
  setMandate: (accountId: number, bankAccount: string, limit: number) =>
    api.patch<{ account: Account }>(`/api/accounts/${accountId}/mandate`, {
      bank_account: bankAccount,
      limit,
    }),
  openAccount: (investorId: number, input: OpenAccountInput) =>
    api.post<{
      account: Account;
      investor: Investor;
      agreement: InvestorDocument | null;
    }>(`/api/investors/${investorId}/open-account`, input),
  performance: (accountId: number, benchmarkIsin?: string) =>
    api.get<{ performance: AccountPerformance }>(
      `/api/accounts/${accountId}/performance${benchmarkIsin ? `?benchmark=${encodeURIComponent(benchmarkIsin)}` : ""}`,
    ),
  savingsPlans: (investorId: number) =>
    api.get<{ savings_plans: SavingsPlan[] }>(
      `/api/investors/${investorId}/savings-plans`,
    ),
  updateSavingsPlan: (planId: number, patch: SavingsPlanPatch) =>
    api.put<{ savings_plan: SavingsPlan; quantfolio_error: string | null }>(
      `/api/savings-plans/${planId}`,
      patch,
    ),
  runSavingsInstallment: (planId: number, opts?: { due_date?: string; force?: boolean }) =>
    api.post<{ result: InstallmentResult; savings_plan: SavingsPlan }>(
      `/api/savings-plans/${planId}/run`,
      opts ?? {},
    ),
  cancelSavingsPlan: (planId: number) =>
    api.delete<{ savings_plan: SavingsPlan; quantfolio_error: string | null }>(
      `/api/savings-plans/${planId}`,
    ),
  postDividend: (isin: string, perUnit: number, date?: string) =>
    api.post<{
      dividend: {
        instrument: string;
        isin: string;
        per_unit: number;
        accounts_credited: number;
        total_paid: number;
      };
    }>("/api/dividends", { isin, per_unit: perUnit, date }),
  generateReport: (investorId: number) =>
    api.post<{ document: InvestorDocument }>(
      `/api/investors/${investorId}/reports`,
      {},
    ),
  accountsForInvestor: (investorId: number) =>
    api.get<{ accounts: Account[] }>(`/api/investors/${investorId}/accounts`),
  createAccount: (investorId: number, name: string, currency: string) =>
    api.post<{ account: Account }>(`/api/investors/${investorId}/accounts`, {
      name,
      currency,
    }),
  instruments: () => api.get<{ instruments: Instrument[] }>("/api/instruments"),
};

// ---------- Orders ----------
export type OrderSide = "buy" | "sell" | "switch";
export type QuantityMode = "units" | "amount" | "percent";
export type OrderStatus =
  | "draft"
  | "submitted"
  | "partially_filled"
  | "executed"
  | "cancelled"
  | "rejected";
export type OrderSource = "manual" | "quantfolio";

export interface Order {
  id: number;
  account_id: number;
  side: OrderSide;
  isin: string;
  instrument_name: string;
  to_isin: string | null;
  to_instrument_name: string | null;
  quantity_mode: QuantityMode;
  quantity: number;
  limit_price: number | null;
  status: OrderStatus;
  status_reason: string | null;
  source: OrderSource;
  external_ref: string | null;
  note: string | null;
  target_units: number | null;
  executed_units: number | null;
  executed_price: number | null;
  executed_value: number | null;
  executed_to_units: number | null;
  executed_to_price: number | null;
  created_at: string;
  updated_at: string;
  executed_at: string | null;
  // Joined context
  account_name: string;
  account_currency: string;
  investor_id: number;
  investor_name: string;
}

export interface OrderInput {
  account_id: number;
  side: OrderSide;
  isin: string;
  to_isin?: string | null;
  quantity_mode: QuantityMode;
  quantity: number;
  limit_price?: number | null;
  note?: string | null;
}

export interface OrderFilter {
  status?: OrderStatus;
  account_id?: number;
  investor_id?: number;
}

export const orders = {
  list: (filter: OrderFilter = {}) => {
    const qs = new URLSearchParams();
    if (filter.status) qs.set("status", filter.status);
    if (filter.account_id) qs.set("account_id", String(filter.account_id));
    if (filter.investor_id) qs.set("investor_id", String(filter.investor_id));
    const suffix = qs.toString() ? `?${qs}` : "";
    return api.get<{ orders: Order[] }>(`/api/orders${suffix}`);
  },
  get: (id: number) => api.get<{ order: Order }>(`/api/orders/${id}`),
  create: (input: OrderInput) =>
    api.post<{ order: Order }>("/api/orders", input),
  setStatus: (
    id: number,
    status: OrderStatus,
    opts: { fillPercent?: number; reason?: string; overrideReason?: string } = {},
  ) =>
    api.patch<{ order: Order }>(`/api/orders/${id}/status`, {
      status,
      fill_percent: opts.fillPercent,
      reason: opts.reason,
      override_reason: opts.overrideReason,
    }),
  events: (id: number) =>
    api.get<{ events: OrderEvent[] }>(`/api/orders/${id}/events`),
  checks: (id: number) =>
    api.get<{ checks: OrderCheck[] }>(`/api/orders/${id}/checks`),
};

export interface OrderCheck {
  check: string;
  label: string;
  outcome: "pass" | "warn" | "block";
  detail: string;
}

export interface OrderEvent {
  id: number;
  order_id: number;
  event: string;
  actor: string;
  payload: Record<string, unknown> | null;
  created_at: string;
}

export interface McpToolField {
  name: string;
  type: string;
  required: boolean;
  description?: string;
  enumValues?: string[];
}

export interface McpToolSummary {
  name: string;
  title: string;
  description: string;
  fields: McpToolField[];
}

export interface McpServerSummary {
  id: string;
  label: string;
  path: string;
  instructions: string;
  tools: McpToolSummary[];
}

export const mcp = {
  list: () => api.get<{ servers: McpServerSummary[] }>("/api/mcp"),
};

export type FlowCheckStatus = "pending" | "executed" | "canceled";

export interface FlowCheck {
  id: number;
  flow_instance_id: string;
  flow_name: string | null;
  client_id: string | null;
  client_name: string | null;
  client_email: string | null;
  qf_status: string | null;
  external_status: FlowCheckStatus;
  external_status_info: string | null;
  decision_actor: string | null;
  decided_at: string | null;
  last_event_id: number | null;
  payload: string | null;
  created_at: string;
  updated_at: string;
}

export const flowChecks = {
  list: (status?: FlowCheckStatus) =>
    api.get<{ checks: FlowCheck[] }>(
      status ? `/api/flow-checks?status=${status}` : "/api/flow-checks",
    ),
  get: (id: number) => api.get<{ check: FlowCheck }>(`/api/flow-checks/${id}`),
  decide: (id: number, decision: "approve" | "reject", info?: string) =>
    api.post<{ check: FlowCheck }>(`/api/flow-checks/${id}/decision`, {
      decision,
      info,
    }),
};

export type WebhookEventStatus = "received" | "processed" | "ignored" | "failed";

export interface WebhookEvent {
  id: number;
  source: string;
  event_type: string | null;
  delivery_id: string | null;
  external_id: string | null;
  signature_valid: number | null;
  status: WebhookEventStatus;
  error: string | null;
  payload: string | null;
  headers: string | null;
  received_at: string;
  processed_at: string | null;
}

export interface WebhookEndpoint {
  key: string;
  label: string;
  path: string;
  source: string;
  description: string;
  actionLabel: string;
  kind: "flow-check" | "log-only";
  secretEnv: string;
  signatureHeader: string;
  deliveryHeader: string;
  productionUrl: string;
  testUrl: string;
  secretConfigured: boolean;
}

export const webhookLog = {
  list: (limit?: number) =>
    api.get<{ events: WebhookEvent[]; total: number }>(
      limit ? `/api/webhook-events?limit=${limit}` : "/api/webhook-events",
    ),
  clear: () => api.delete<{ ok: true; deleted: number }>("/api/webhook-events"),
  endpoints: () => api.get<{ endpoints: WebhookEndpoint[] }>("/api/webhook-endpoints"),
};

export const activities = {
  list: (investorId: number) =>
    api.get<{ activities: InvestorActivity[] }>(
      `/api/investors/${investorId}/activities`,
    ),
  create: (investorId: number, input: ActivityInput) =>
    api.post<{ activity: InvestorActivity }>(
      `/api/investors/${investorId}/activities`,
      input,
    ),
  update: (
    investorId: number,
    id: number,
    input: Partial<ActivityInput>,
  ) =>
    api.patch<{ activity: InvestorActivity }>(
      `/api/investors/${investorId}/activities/${id}`,
      input,
    ),
  remove: (investorId: number, id: number) =>
    api.delete<{ ok: true }>(
      `/api/investors/${investorId}/activities/${id}`,
    ),
};
